(function (window) {
  'use strict';

  var gsap = window.gsap;
  var timeline = null;
  var tween = null;
  var pendingCount = 0;
  var booted = false;
  var labelTimes = { s0: 0, s1: 1.54, s2: 2.3, s3: 2.75 };
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function clipValue(v) {
    var hidden = Math.max(0, Math.min(100, (1 - v) * 100));
    return 'inset(0 ' + hidden + '% 0 0)';
  }

  function tweenChange(tl, target, fromVars, toVars, start, duration, ease) {
    if (duration <= 0.0001) return;
    toVars.duration = duration;
    toVars.ease = ease || 'none';
    toVars.immediateRender = false;
    tl.fromTo(target, fromVars, toVars, start);
  }

  function labelName(n) {
    return 's' + Math.max(0, Math.min(3, Number(n) || 0));
  }

  function setCount(n) {
    pendingCount = Math.max(0, Math.min(3, Number(n) || 0));
    if (!timeline) return;
    if (tween) {
      tween.kill();
      tween = null;
    }
    var target = labelTimes[labelName(pendingCount)];
    if (target == null) target = 0;
    if (reduceMotion || !booted) {
      timeline.pause();
      timeline.seek(target);
      return;
    }
    var delta = Math.abs(timeline.time() - target);
    if (delta < 0.02) {
      timeline.pause();
      timeline.seek(target);
      return;
    }
    tween = timeline.tweenTo(target, { duration: delta, ease: 'none' });
  }

  function buildTimeline(layout, nodes, paths) {
    var tracks = (layout.motion && layout.motion.tracks) || {};
    var labels = (layout.motion && layout.motion.labels) || {
      s0: 0,
      s1: 1.54,
      s2: 2.3,
      s3: 2.75,
    };
    var duration = labels.s3 || (layout.motion && layout.motion.duration) || 2.75;
    labelTimes = labels;
    var tl = gsap.timeline({
      paused: true,
      defaults: { immediateRender: false },
    });
    tl.to({}, { duration: duration, ease: 'none' }, 0);

    Object.keys(labels).forEach(function (name) {
      tl.addLabel(name, labels[name]);
    });

    layout.layers.forEach(function (layer) {
      var el = nodes[layer.id];
      var spec = tracks[layer.id] || {};
      if (!el) return;

      gsap.set(el, { transformOrigin: layer.origin || '50% 50%' });

      if (spec.scale && spec.scale.length) {
        gsap.set(el, { scale: spec.scale[0].v });
        var scaleKeys = spec.scale;
        var prevScale = scaleKeys[0];
        for (var s = 1; s < scaleKeys.length; s++) {
          var scaleKey = scaleKeys[s];
          var scaleDur = scaleKey.t - prevScale.t;
          if (scaleDur > 0.0001 && scaleKey.v !== prevScale.v) {
            tweenChange(
              tl,
              el,
              { scale: prevScale.v },
              { scale: scaleKey.v },
              prevScale.t,
              scaleDur,
              scaleKey.ease
            );
          }
          prevScale = scaleKey;
        }
      } else {
        gsap.set(el, { scale: 1 });
      }

      if (spec.xPercent && spec.xPercent.length) {
        gsap.set(el, { xPercent: spec.xPercent[0].v });
        var xKeys = spec.xPercent;
        var prevX = xKeys[0];
        for (var x = 1; x < xKeys.length; x++) {
          var xKey = xKeys[x];
          var xDur = xKey.t - prevX.t;
          if (xDur > 0.0001 && xKey.v !== prevX.v) {
            tweenChange(
              tl,
              el,
              { xPercent: prevX.v },
              { xPercent: xKey.v },
              prevX.t,
              xDur,
              xKey.ease
            );
          }
          prevX = xKey;
        }
      }

      if (spec.autoAlpha && spec.autoAlpha.length) {
        gsap.set(el, { autoAlpha: spec.autoAlpha[0].v });
        var aKeys = spec.autoAlpha;
        var prevA = aKeys[0];
        for (var a = 1; a < aKeys.length; a++) {
          var aKey = aKeys[a];
          var aDur = aKey.t - prevA.t;
          if (aDur > 0.0001 && aKey.v !== prevA.v) {
            tweenChange(
              tl,
              el,
              { autoAlpha: prevA.v },
              { autoAlpha: aKey.v },
              prevA.t,
              aDur,
              aKey.ease
            );
          }
          prevA = aKey;
        }
      }

      if (layer.kind === 'cable' && spec.draw && spec.draw.length) {
        var pathEl = paths[layer.id];
        var isStroke = layer.draw === 'stroke' && pathEl;
        var proxy = { v: spec.draw[0].v };
        var applyTrim = function () {
          var v = proxy.v;
          if (isStroke) {
            gsap.set(pathEl, {
              attr: { 'stroke-dasharray': v >= 0.999 ? '1 0' : v + ' 1' },
            });
            gsap.set(el, { autoAlpha: v > 0.001 ? 1 : 0 });
          } else {
            gsap.set(el, {
              clipPath: clipValue(v),
              autoAlpha: v > 0.001 ? 1 : 0,
            });
          }
        };
        applyTrim();
        var drawKeys = spec.draw;
        var prevDraw = drawKeys[0];
        for (var d = 1; d < drawKeys.length; d++) {
          var drawKey = drawKeys[d];
          var drawDur = drawKey.t - prevDraw.t;
          if (drawDur > 0.0001 && drawKey.v !== prevDraw.v) {
            tweenChange(
              tl,
              proxy,
              { v: prevDraw.v },
              { v: drawKey.v, onUpdate: applyTrim },
              prevDraw.t,
              drawDur,
              drawKey.ease
            );
          }
          prevDraw = drawKey;
        }
      }
    });

    return tl;
  }

  function mount(root) {
    if (!root || !gsap) return;
    var stage = root.querySelector('[data-connector-stage]');
    var layoutUrl = root.getAttribute('data-layout-url');
    var files = {};
    try {
      files = JSON.parse(root.getAttribute('data-files') || '{}');
    } catch (err) {
      files = {};
    }
    if (!stage || !layoutUrl) return;

    if (timeline) {
      timeline.kill();
      timeline = null;
    }
    booted = false;

    fetch(layoutUrl, { cache: 'no-store' })
      .then(function (res) {
        return res.json();
      })
      .then(function (layout) {
        var canvas = layout.canvas || { w: 3482, h: 3190 };
        var view = layout.view || { x: 0, y: 0, w: canvas.w, h: canvas.h };
        stage.style.aspectRatio = view.w + ' / ' + view.h;
        function fitStage() {
          var maxW = Math.max(8, root.clientWidth - 24);
          var maxH = Math.max(8, root.clientHeight - 24);
          if (maxW < 8 || maxH < 8) return;
          var ratio = view.h / view.w;
          var width = maxW;
          var height = width * ratio;
          if (height > maxH) {
            height = maxH;
            width = height / ratio;
          }
          stage.style.width = Math.floor(width) + 'px';
          stage.style.height = Math.floor(height) + 'px';
        }
        fitStage();
        if (window.ResizeObserver) {
          var observer = new ResizeObserver(function () {
            fitStage();
          });
          observer.observe(root);
        }
        stage.innerHTML = '';
        var nodes = {};
        var paths = {};
        var layers = layout.layers.slice().sort(function (a, b) {
          return a.z - b.z;
        });

        layers.forEach(function (layer) {
          var el = document.createElement('div');
          el.className =
            'umbrella-configurator__connector-layer' + (layer.kind === 'cable' ? ' is-cable' : '');
          el.style.zIndex = String(layer.z);
          el.style.left = ((layer.x - view.x) / view.w) * 100 + '%';
          el.style.top = ((layer.y - view.y) / view.h) * 100 + '%';
          el.style.width = (layer.w / view.w) * 100 + '%';
          el.style.height = (layer.h / view.h) * 100 + '%';

          if (layer.kind === 'cable' && layer.svg) {
            var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            svg.setAttribute('viewBox', layer.svg.viewBox);
            svg.setAttribute('preserveAspectRatio', layer.draw === 'stroke' ? 'xMidYMid meet' : 'none');
            svg.setAttribute('overflow', 'visible');
            svg.style.overflow = 'visible';
            var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            path.setAttribute('d', layer.svg.d);
            path.setAttribute('pathLength', '1');
            if (layer.svg.fill || layer.draw === 'clip') {
              path.setAttribute('fill', 'black');
              path.setAttribute('stroke', 'none');
            } else {
              path.setAttribute('fill', 'none');
              path.setAttribute('stroke', 'black');
              path.setAttribute('stroke-width', String(layer.svg.strokeWidth || 80));
              path.setAttribute('stroke-linecap', layer.svg.linecap || 'butt');
              path.setAttribute('stroke-linejoin', 'miter');
              path.setAttribute('stroke-dasharray', '0 1');
            }
            svg.appendChild(path);
            el.appendChild(svg);
            paths[layer.id] = path;
          } else {
            var img = document.createElement('img');
            img.src = files[layer.file] || files[layer.id] || '';
            img.alt = '';
            img.draggable = false;
            el.appendChild(img);
          }

          stage.appendChild(el);
          nodes[layer.id] = el;
        });

        fitStage();
        timeline = buildTimeline(layout, nodes, paths);
        booted = true;
        setCount(pendingCount);
      })
      .catch(function () {
        if (!stage.children.length) root.hidden = true;
      });
  }

  window.LeavesConnector = {
    mount: mount,
    setCount: setCount,
  };
})(window);
